import { FormProvider, useForm } from 'react-hook-form'

import { AssetConcentrationReportRead as AssetConcentrationReport } from '../../../api'
import {
  Card,
  CheckboxField,
  RadioFieldSet,
  SelectField,
  ToggleField,
  Typography
} from '../../../components/core-v2'
import AssetConcentrationTable from '../../../components/core-v2/organisms/asset-concentration-table'

const ALL_TRADES = { label: 'All trades', value: 'all' }

export type AssetConcentrationStatelessProps = {
  report: AssetConcentrationReport
}

const AssetConcentrationStateless = ({ report }: AssetConcentrationStatelessProps) => {
  const form = useForm({
    defaultValues: {
      filter: ALL_TRADES.value,
      compliant: true,
      closeToLimit: true,
      breached: true,
      viewByFundManager: true,
      viewAsValueOrPercentage: 'value'
    }
  })
  const [
    filterValue,
    compliantValue,
    closeToLimitValue,
    breachedValue,
    viewByFundManagerValue,
    viewAsValueOrPercentageValue
  ] = form.watch([
    'filter',
    'compliant',
    'closeToLimit',
    'breached',
    'viewByFundManager',
    'viewAsValueOrPercentage'
  ])
  const filterOptions = [ALL_TRADES]
  const entriesCount = report?.entries?.length
  const isValue = viewAsValueOrPercentageValue === 'value'

  const currenciesMap = new Set<string>()
  if (entriesCount > 0) {
    report.entries.forEach(row => currenciesMap.add(row.currency))
  }
  const currencies = Array.from(currenciesMap)
  currencies.forEach(item => filterOptions.push({ label: item, value: item }))

  let filteredEntries = report.entries
  if (filterValue !== ALL_TRADES.value) {
    filteredEntries = report.entries?.filter(item => item.currency === filterValue)
  }

  filteredEntries = filteredEntries
    ?.filter(row => {
      const breached =
        row.amountInvestedWithGapRisk / row.aum >= 0.1 ||
        row.amountInvestedWithoutGapRisk / row.aum >= 0.1 ||
        row.amountInvestedTotal / row.aum >= 0.2

      const closeToLimit =
        !breached &&
        (row.amountInvestedWithGapRisk / row.aum >= 0.05 ||
          row.amountInvestedWithoutGapRisk / row.aum >= 0.05 ||
          row.amountInvestedTotal / row.aum >= 0.1)

      return (
        (breachedValue && breached) ||
        (closeToLimitValue && !breached && closeToLimit) ||
        (compliantValue && !breached && !closeToLimit)
      )
    })
    .map(entry => {
      const amountInvestedWithGapRiskValue = entry.amountInvestedWithGapRisk
      const amountInvestedWithGapRiskPercentage = entry.amountInvestedWithGapRisk / entry.aum

      const amountInvestedWithoutGapRiskValue = entry.amountInvestedWithoutGapRisk
      const amountInvestedWithoutGapRiskPercentage = entry.amountInvestedWithoutGapRisk / entry.aum

      const amountInvestedTotalValue = entry.amountInvestedTotal
      const amountInvestedTotalPercentage = entry.amountInvestedTotal / entry.aum
      return {
        ...entry,
        name: entry.assetName,
        amountInvestedWithGapRisk: isValue
          ? amountInvestedWithGapRiskValue
          : amountInvestedWithGapRiskPercentage,
        amountInvestedWithGapRiskValue,
        amountInvestedWithGapRiskPercentage,
        amountInvestedWithoutGapRisk: isValue
          ? amountInvestedWithoutGapRiskValue
          : amountInvestedWithoutGapRiskPercentage,
        amountInvestedWithoutGapRiskValue,
        amountInvestedWithoutGapRiskPercentage,
        amountInvestedTotal: isValue ? amountInvestedTotalValue : amountInvestedTotalPercentage,
        amountInvestedTotalValue,
        amountInvestedTotalPercentage
      }
    })

  const dataByFundManager: Record<string, any> = filteredEntries.reduce((acc, entry) => {
    const existingFundManager = acc[entry.fundManager]
    if (existingFundManager) {
      existingFundManager.push(entry)
    } else {
      acc[entry.fundManager] = [entry]
    }

    return acc
  }, {})

  const rowsByFundManager = Object.entries(dataByFundManager).reduce(
    (acc, [fundManager, entries]) => {
      const totals = entries.reduce(
        (acc, entry) => {
          acc.aum += entry.aum
          acc.amountInvestedWithGapRiskValue += entry.amountInvestedWithGapRiskValue
          acc.amountInvestedWithGapRiskPercentage += entry.amountInvestedWithGapRiskPercentage
          acc.amountInvestedWithoutGapRiskValue += entry.amountInvestedWithoutGapRiskValue
          acc.amountInvestedWithoutGapRiskPercentage += entry.amountInvestedWithoutGapRiskPercentage
          acc.amountInvestedTotalValue += entry.amountInvestedTotalValue
          acc.amountInvestedTotalPercentage += entry.amountInvestedTotalPercentage
          return acc
        },
        {
          aum: 0,
          amountInvestedWithGapRiskValue: 0,
          amountInvestedWithGapRiskPercentage: 0,
          amountInvestedWithoutGapRiskValue: 0,
          amountInvestedWithoutGapRiskPercentage: 0,
          amountInvestedTotalValue: 0,
          amountInvestedTotalPercentage: 0
        }
      )

      acc.push({
        name: fundManager,
        aum: totals.aum,
        amountInvestedWithGapRisk: isValue
          ? totals.amountInvestedWithGapRiskValue
          : totals.amountInvestedWithGapRiskValue / totals.aum,
        amountInvestedWithoutGapRisk: isValue
          ? totals.amountInvestedWithoutGapRiskValue
          : totals.amountInvestedWithoutGapRiskValue / totals.aum,
        amountInvestedTotal: isValue
          ? totals.amountInvestedTotalValue
          : totals.amountInvestedTotalValue / totals.aum,
        currency: entries[0].currency
      })

      entries.forEach(entry => acc.push({ ...entry, name: entry.assetName }))

      return acc
    },
    []
  )

  const reportCountMessage = `Showing ${filteredEntries?.length} of ${entriesCount}`

  return (
    <FormProvider {...form}>
      <Card.Wrapper spacing="sm">
        <div className="d-flex align-items-center">
          <SelectField id="filter" label="Asset concentration" options={filterOptions} />
          <div className="d-flex flex-direction-column ml-auto">
            <div className="d-flex align-items-center">
              <CheckboxField className="ml-auto mr-1" id="compliant" label="Compliant" />
              <CheckboxField className="mr-1" id="closeToLimit" label="Close to limit" />
              <CheckboxField id="breached" label="Breached" />
            </div>
            <div className="d-flex align-items-center">
              <RadioFieldSet
                isLegendInline
                legend="View as"
                name="viewAsValueOrPercentage"
                options={[
                  { label: 'Value', value: 'value' },
                  { label: '% of AUM', value: 'percentage' }
                ]}
              />
              <ToggleField className="ml-2" id="viewByFundManager" label="View by fund manager" />
            </div>
          </div>
        </div>
        <Typography className="my-1" variant="sh3">
          {reportCountMessage}
        </Typography>
        <AssetConcentrationTable
          data={viewByFundManagerValue ? rowsByFundManager : filteredEntries}
          viewAsValueOrPercentage={viewAsValueOrPercentageValue as 'value' | 'percentage'}
        />
      </Card.Wrapper>
    </FormProvider>
  )
}

export default AssetConcentrationStateless
